/* ==========================================================================
   La Maison — theme behaviour
   Header, drawers (bag / search / menu), Ajax cart, product form, rails,
   collection filters, recommendations, hero video framing and film overlay.
   ========================================================================== */

(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const T = window.theme || { strings: {}, routes: {} };
  const S = T.strings;

  /* ---------- Money ---------- */

  const formatMoney = (cents, format = T.moneyFormat || '€{{amount_with_comma_separator}}') => {
    const value = Number(cents) / 100;
    const group = (n, thousands, decimal, places = 2) => {
      const [int, dec] = n.toFixed(places).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return group(value, ',', '.');
        case 'amount_no_decimals': return group(value, ',', '.', 0);
        case 'amount_with_comma_separator': return group(value, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, '.', ',', 0);
        case 'amount_with_apostrophe_separator': return group(value, "'", '.');
        default: return group(value, '.', ',');
      }
    });
  };
  const escapeHTML = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const sizedImage = (url, width) => (url ? url + (url.includes('?') ? '&' : '?') + 'width=' + width : '');

  /* ---------- Toast ---------- */

  const toast = (msg) => {
    const t = $('#toast');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(toast.timer);
    toast.timer = setTimeout(() => t.classList.remove('is-visible'), 2400);
  };

  /* ---------- Overlays (bag, search, menu) ---------- */

  let activePanel = null;
  let lastFocus = null;

  const openPanel = (id) => {
    closePanel();
    const el = document.getElementById(id);
    if (!el) return;
    lastFocus = document.activeElement;
    activePanel = el;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    $('.scrim')?.classList.add('is-open');
    document.body.classList.add('is-locked');
    if (id === 'search') setTimeout(() => $('[data-search-input]')?.focus(), 50);
    else el.querySelector('[data-close]')?.focus();
    if (id === 'cart') refreshCart();
  };

  const closePanel = () => {
    if (!activePanel) return;
    activePanel.classList.remove('is-open');
    activePanel.setAttribute('aria-hidden', 'true');
    $('.scrim')?.classList.remove('is-open');
    document.body.classList.remove('is-locked');
    activePanel = null;
    lastFocus?.focus?.();
  };

  const initOverlays = () => {
    // The mobile menu is rendered inside the header section; move it to <body> so it
    // stacks above the scrim instead of inside the sticky header's stacking context.
    const menu = $('#menu');
    if (menu) document.body.appendChild(menu);

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open]');
      if (opener) { e.preventDefault(); openPanel(opener.dataset.open); return; }
      if (e.target.closest('[data-close]')) closePanel();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closePanel(); });
  };

  /* ---------- Predictive search ---------- */

  const initSearch = () => {
    const input = $('[data-search-input]');
    const results = $('[data-search-results]');
    const hint = $('[data-search-hint]');
    if (!input || !results) return;
    const defaultHint = hint?.textContent || '';
    let controller;

    const run = async () => {
      const q = input.value.trim();
      controller?.abort();
      if (!q) { results.innerHTML = ''; if (hint) hint.textContent = defaultHint; return; }
      controller = new AbortController();
      const url = `${T.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
      try {
        const html = await fetch(url, { signal: controller.signal }).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const wrap = doc.querySelector('[data-predictive-results]');
        const count = Number(wrap?.dataset.count || 0);
        results.innerHTML = wrap ? wrap.innerHTML : '';
        if (hint) hint.textContent = count ? S.results : S.noResults;
      } catch (err) {
        if (err.name !== 'AbortError') results.innerHTML = '';
      }
    };

    let timer;
    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(run, 180); });
  };

  /* ---------- Cart (Ajax API) ---------- */

  const cartLines = () => $('[data-cart-lines]');
  const cartFoot = () => $('[data-cart-foot]');

  const updateCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      el.dataset.count = count;
      el.innerHTML = count ? `<span>${count}</span>` : '';
    });
    $$('[data-cart-qty]').forEach((el) => (el.textContent = count));
  };

  const renderCart = (cart) => {
    updateCount(cart.item_count);
    const lines = cartLines();
    const foot = cartFoot();
    if (!lines || !foot) return;

    if (!cart.item_count) {
      lines.innerHTML = `<div class="drawer__empty"><p>${escapeHTML(S.cartEmpty)}</p><a class="btn btn--dark" href="${T.routes.root}collections/all">${escapeHTML(S.shopCollection)}</a></div>`;
      foot.innerHTML = '';
      return;
    }

    lines.innerHTML = cart.items.map((item) => {
      const variant = item.product_has_only_default_variant ? '' : `<div class="line__meta">${escapeHTML(item.variant_title || '')}</div>`;
      return `
        <div class="line">
          <a class="line__img" href="${item.url}">${item.image ? `<img src="${sizedImage(item.image, 300)}" alt="${escapeHTML(item.product_title)}" loading="lazy">` : ''}</a>
          <div>
            <a class="line__name" href="${item.url}">${escapeHTML(item.product_title)}</a>
            ${variant}
            <div class="line__qty">
              <button type="button" data-line-change="${item.key}" data-qty="${item.quantity - 1}" aria-label="−">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-line-change="${item.key}" data-qty="${item.quantity + 1}" aria-label="+">+</button>
            </div>
          </div>
          <div class="line__right">
            <span>${formatMoney(item.final_line_price)}</span>
            <button type="button" class="line__remove" data-line-change="${item.key}" data-qty="0">${escapeHTML(S.remove)}</button>
          </div>
        </div>`;
    }).join('');

    const threshold = Number(T.freeShippingThreshold || 0);
    const remaining = Math.max(0, threshold - cart.total_price);
    const progress = threshold ? `
      <div class="ship-progress">
        <p>${remaining ? escapeHTML(S.shippingAway).replace(/\{\{\s*amount\s*\}\}/, formatMoney(remaining)) : escapeHTML(S.shippingFree)}</p>
        <div class="ship-progress__bar"><span style="width:${Math.min(100, (cart.total_price / threshold) * 100)}%"></span></div>
      </div>` : '';

    foot.innerHTML = `
      ${progress}
      <div class="drawer__total"><span>${escapeHTML(S.subtotal)}</span><span>${formatMoney(cart.total_price)}</span></div>
      <button type="submit" form="CartCheckout" class="btn btn--dark btn--block">${escapeHTML(S.checkout)}</button>
      <p class="drawer__note">${escapeHTML(S.taxNote)}</p>`;
  };

  const refreshCart = async () => {
    try { renderCart(await fetch(`${T.routes.cart}.js`).then((r) => r.json())); } catch { /* keep current state */ }
  };

  const addToCart = async (variantId, quantity = 1) => {
    const res = await fetch(`${T.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || S.error);
    return data;
  };

  const initCart = () => {
    cartLines()?.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-line-change]');
      if (!btn) return;
      btn.disabled = true;
      try {
        const res = await fetch(`${T.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: btn.dataset.lineChange, quantity: Number(btn.dataset.qty) }),
        });
        const cart = await res.json();
        if (!res.ok) throw new Error(cart.description || S.error);
        renderCart(cart);
      } catch (err) {
        toast(err.message);
        refreshCart();
      }
    });
  };

  /* ---------- Product cards (quick add) ---------- */

  const initCards = () => {
    document.addEventListener('click', async (e) => {
      const quick = e.target.closest('.card__quick');
      if (quick) {
        const card = quick.closest('.card');
        $$('.card.is-quick').forEach((c) => {
          if (c !== card) { c.classList.remove('is-quick'); $('.card__quick', c)?.setAttribute('aria-expanded', 'false'); }
        });
        const open = card.classList.toggle('is-quick');
        quick.setAttribute('aria-expanded', String(open));
        return;
      }

      const add = e.target.closest('[data-quick-add]');
      if (!add) return;
      const card = add.closest('.card');
      add.disabled = true;
      try {
        await addToCart(add.dataset.quickAdd);
        add.classList.add('is-added');
        const name = $('.card__name', card)?.textContent.trim() || '';
        const size = add.dataset.variantTitle ? ` · ${add.dataset.variantTitle}` : '';
        toast(`${name}${size} ${S.added}`);
        refreshCart();
        setTimeout(() => { card.classList.remove('is-quick'); add.classList.remove('is-added'); }, 700);
      } catch (err) {
        toast(err.message);
      } finally {
        add.disabled = false;
      }
    });
  };

  /* ---------- Rails ---------- */

  const initRails = (root = document) => {
    $$('.rail', root).forEach((rail) => {
      if (rail.dataset.ready) return;
      rail.dataset.ready = '1';
      const track = $('.rail__track', rail);
      const prev = $('.rail__nav--prev', rail);
      const next = $('.rail__nav--next', rail);
      if (!track || !prev || !next) return;
      const step = () => track.clientWidth * 0.75;
      const update = () => {
        prev.disabled = track.scrollLeft < 8;
        next.disabled = track.scrollLeft + track.clientWidth > track.scrollWidth - 8;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      update();
    });
  };

  /* ---------- Reveal on scroll ---------- */

  const initReveal = () => {
    const items = $$('.reveal');
    if (!('IntersectionObserver' in window)) { items.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach((el) => io.observe(el));
  };

  /* ---------- Hero: pause off-screen, mobile per-shot framing, film overlay ---------- */

  const initHero = () => {
    const frame = $('.hero-b__frame');
    const video = frame && $('video.hero__media', frame);
    if (!video) return;

    // Show the video only once frames are actually playing; until then the still image shows.
    const markPlaying = () => frame.classList.add('is-playing');
    video.addEventListener('playing', markPlaying);
    if (!video.paused && video.readyState > 2) markPlaying();

    let inView = true;
    const tryPlay = () => {
      if (!inView || document.querySelector('.film.is-open') || video.error) return;
      video.play().catch(() => {});
    };
    new IntersectionObserver(([en]) => {
      inView = en.isIntersecting;
      inView ? tryPlay() : video.pause();
    }).observe(video);

    // iOS Low Power Mode (and some data-saver modes) block autoplay but allow playback after
    // any user gesture: start the video on the first touch or click anywhere on the page.
    const onGesture = () => {
      if (video.paused) tryPlay();
      if (!video.paused) ['touchend', 'click'].forEach((t) => document.removeEventListener(t, onGesture, true));
    };
    ['touchend', 'click'].forEach((t) => document.addEventListener(t, onGesture, { capture: true, passive: true }));

    /*
     * Mobile: the 16:9 video is cropped to fill the tall frame. data-reframe lists shots that
     * need another crop: "start-end:focus:zoom, ..." (focus 0..1 across the width; zoom is an
     * extra zoom factor, or "fit" for the whole frame). Switched on the exact cut frame.
     */
    if (frame.dataset.reframe) {
      const shots = frame.dataset.reframe.split(',').map((r) => {
        const [range, focus = '0.5', zoom = '1'] = r.trim().split(':');
        const [a, b] = range.split('-').map(Number);
        return { a, b, focus: Number(focus), zoom: zoom === 'fit' ? 'fit' : Number(zoom) };
      }).filter((s) => !Number.isNaN(s.a) && !Number.isNaN(s.b));
      const mobile = matchMedia('(max-width: 860px)');
      let cover = 1;

      const measure = () => { cover = Math.max(1, frame.clientHeight / (frame.clientWidth * 9 / 16)); };
      const render = (t = video.currentTime) => {
        if (!mobile.matches) { video.style.transform = ''; return; }
        const shot = shots.find((s) => t >= s.a && t < s.b);
        const scale = shot?.zoom === 'fit' ? 1 : cover * (shot?.zoom ?? 1);
        const half = 0.5 / scale;
        const f = Math.min(1 - half, Math.max(half, shot?.focus ?? 0.5));
        const shift = (0.5 - f) * frame.clientWidth * scale;
        video.style.transform = `translateX(${shift.toFixed(1)}px) scale(${scale.toFixed(4)})`;
      };

      if ('requestVideoFrameCallback' in video) {
        const onFrame = (_, meta) => { render(meta.mediaTime); video.requestVideoFrameCallback(onFrame); };
        video.requestVideoFrameCallback(onFrame);
      } else {
        video.addEventListener('timeupdate', () => render());
      }
      video.addEventListener('seeked', () => render());
      window.addEventListener('resize', () => { measure(); render(); });
      measure();
      render();
    }

    const trigger = $('[data-film]');
    if (!trigger) return;
    const sources = $$('source', video).map((s) => s.outerHTML).join('') || (video.src ? `<source src="${video.src}" type="video/mp4">` : '');
    document.body.insertAdjacentHTML('beforeend', `
      <div class="film" id="film" role="dialog" aria-modal="true" aria-label="${escapeHTML(trigger.textContent.trim())}" aria-hidden="true">
        <video class="film__video" playsinline controls preload="none">${sources}</video>
        <button type="button" class="film__close" aria-label="Close">${$('[data-close] svg')?.outerHTML || '×'}</button>
      </div>`);
    const film = $('#film');
    const filmVideo = $('.film__video', film);
    const closeBtn = $('.film__close', film);

    const open = () => {
      film.classList.add('is-open');
      film.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      video.pause();
      filmVideo.currentTime = 0;
      filmVideo.muted = false;
      filmVideo.play().catch(() => {});
      closeBtn.focus();
    };
    const close = () => {
      if (!film.classList.contains('is-open')) return;
      film.classList.remove('is-open');
      film.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      filmVideo.pause();
      video.play().catch(() => {});
      trigger.focus();
    };
    trigger.addEventListener('click', open);
    closeBtn.addEventListener('click', close);
    filmVideo.addEventListener('ended', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  };

  /* ---------- Collection filters (type / colour, client-side) ---------- */

  const initCollection = () => {
    const filters = $('[data-filters]');
    const grid = $('[data-grid]');
    if (!filters || !grid) return;
    const params = new URLSearchParams(location.search);
    const handleize = (s) => String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    let type = handleize(params.get('type')) || 'all';
    let colour = handleize(params.get('colour')) || 'all';
    const count = $('[data-count]');
    const empty = $('[data-filter-empty]');
    const title = $('[data-shop-title]');
    const itemsLabel = count?.textContent.replace(/^\d+\s*/, '') || '';

    const apply = () => {
      $$('[data-filter-type]', filters).forEach((b) => { const on = b.dataset.filterType === type; b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', String(on)); });
      $$('[data-filter-colour]', filters).forEach((b) => { const on = b.dataset.filterColour === colour; b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', String(on)); });
      let shown = 0;
      $$('.grid__item', grid).forEach((item) => {
        const ok = (type === 'all' || item.dataset.type === type) && (colour === 'all' || item.dataset.colour === colour);
        item.hidden = !ok;
        if (ok) shown++;
      });
      if (count) count.textContent = `${shown} ${shown === 1 ? itemsLabel.replace(/s$/, '') : itemsLabel}`;
      if (empty) empty.hidden = shown > 0;
      if (title) {
        const active = $(`[data-filter-type="${type}"]`, filters);
        title.textContent = type === 'all' || !active ? title.dataset.defaultTitle : active.textContent.trim();
      }
      const q = new URLSearchParams(location.search);
      type === 'all' ? q.delete('type') : q.set('type', type);
      colour === 'all' ? q.delete('colour') : q.set('colour', colour);
      history.replaceState(null, '', q.toString() ? `?${q}` : location.pathname);
    };

    filters.addEventListener('click', (e) => {
      const t = e.target.closest('[data-filter-type]');
      const c = e.target.closest('[data-filter-colour]');
      if (t) type = t.dataset.filterType;
      if (c) colour = c.dataset.filterColour;
      if (t || c) apply();
    });
    apply();
  };

  /* ---------- Product form ---------- */

  const initProduct = () => {
    const section = $('[data-product]');
    if (!section) return;
    const data = JSON.parse($('[data-product-json]', section).textContent);
    const form = $('[data-product-form]', section);
    const input = $('[data-variant-input]', section);
    const addBtn = $('[data-add-button]', section);
    const error = $('[data-form-error]', section);
    const sizes = $('[data-sizes]', section);
    const legends = $$('.size-group legend', section).map((l) => l.textContent.trim());
    const selected = new Array(data.optionCount).fill(null);
    if (data.singleDefault) selected.fill('Default Title');

    const findVariant = () => data.variants.find((v) => v.options.every((o, i) => o === selected[i]));

    const refresh = () => {
      // Mark sizes that are sold out given the other selections.
      $$('[data-option-value]', section).forEach((btn) => {
        const i = Number(btn.dataset.optionIndex);
        const test = selected.slice(); test[i] = btn.dataset.optionValue;
        const possible = data.variants.some((v) => v.available && v.options.every((o, j) => test[j] == null || o === test[j]));
        btn.classList.toggle('is-unavailable', !possible);
      });

      const ready = selected.every(Boolean);
      const variant = ready ? findVariant() : null;
      const missingIndex = selected.findIndex((s) => !s);
      let label;
      if (!ready) {
        label = data.optionCount > 1 ? S.selectOption.replace(/\{\{\s*option\s*\}\}/, (legends[missingIndex] || '').toLowerCase()) : S.selectSize;
      } else if (!variant || !variant.available) {
        label = S.soldOut;
      } else {
        label = `${S.addToBag} — ${formatMoney(variant.price)}`;
        $$('[data-price]').forEach((el) => (el.textContent = formatMoney(variant.price)));
      }
      addBtn.textContent = label;
      addBtn.disabled = ready && (!variant || !variant.available);
      if (variant) {
        input.value = variant.id;
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(null, '', url);
      }
    };

    sizes?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-option-value]');
      if (!btn) return;
      const i = Number(btn.dataset.optionIndex);
      selected[i] = btn.dataset.optionValue;
      $$(`[data-option-index="${i}"]`, section).forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', String(on));
      });
      if (error) error.hidden = true;
      refresh();
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!selected.every(Boolean)) { sizes?.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
      const variant = findVariant();
      if (!variant || !variant.available) return;
      addBtn.disabled = true;
      try {
        await addToCart(variant.id);
        openPanel('cart');
      } catch (err) {
        if (error) { error.textContent = err.message; error.hidden = false; }
      } finally {
        addBtn.disabled = false;
      }
    });

    // Mobile sticky add bar mirrors the main button.
    const bar = $('[data-mobile-bar]');
    const mobileAdd = $('[data-mobile-add]');
    mobileAdd?.addEventListener('click', () => {
      if (selected.every(Boolean)) form.requestSubmit();
      else sizes?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    if (bar && 'IntersectionObserver' in window) {
      new IntersectionObserver(([en]) => bar.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0)).observe(addBtn);
    }

    // Preselect when a variant is in the URL (e.g. shared link).
    const preset = new URLSearchParams(location.search).get('variant');
    const presetVariant = preset && data.variants.find((v) => String(v.id) === preset);
    if (presetVariant && !data.singleDefault) {
      presetVariant.options.forEach((value, i) => {
        const btn = $$(`[data-option-index="${i}"]`, section).find((b) => b.dataset.optionValue === value);
        btn?.click();
      });
    }
    refresh();
  };

  /* ---------- Product recommendations ---------- */

  const initRecommendations = () => {
    const el = $('[data-recommendations]');
    if (!el || !el.dataset.url) return;
    const load = async () => {
      try {
        const html = await fetch(el.dataset.url).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) { el.innerHTML = fresh.innerHTML; initRails(el); }
      } catch { /* section stays empty */ }
    };
    if (!('IntersectionObserver' in window)) { load(); return; }
    const io = new IntersectionObserver(([en]) => { if (en.isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '400px' });
    io.observe(el);
  };

  /* ---------- Smooth scroll (mouse / trackpad) ----------
     Wheel input eases toward its target instead of jumping, like Lenis. Touch devices,
     reduced motion, open overlays, horizontal scrolling and nested scroll areas keep
     native behaviour; keyboard, scrollbar and anchor jumps stay native and re-sync. */

  const initSmoothScroll = () => {
    if (!matchMedia('(pointer: fine)').matches || matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const EASE = 6; // higher = snappier, lower = more glide (9 ≈ Lenis' default)
    let target = window.scrollY;
    let current = window.scrollY;
    let animating = false;
    let lastTime = 0;

    const maxScroll = () => document.documentElement.scrollHeight - window.innerHeight;

    const scrollsItself = (el) => {
      for (let node = el; node && node !== document.body; node = node.parentElement) {
        if (node.matches?.('[data-native-scroll], .drawer, .search, .mobile-menu, .film')) return true;
        const style = getComputedStyle(node);
        if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) return true;
      }
      return false;
    };

    const step = (time) => {
      const dt = Math.min(0.05, (time - (lastTime || time)) / 1000);
      lastTime = time;
      current += (target - current) * (1 - Math.exp(-EASE * dt));
      if (Math.abs(target - current) < 0.5) {
        current = target;
        animating = false;
        lastTime = 0;
      }
      window.scrollTo(0, current);
      if (animating) requestAnimationFrame(step);
    };

    window.addEventListener('wheel', (e) => {
      if (e.ctrlKey || e.defaultPrevented || document.body.classList.contains('is-locked')) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (scrollsItself(e.target)) return;
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1;
      if (!animating) { current = window.scrollY; target = current; }
      target = Math.max(0, Math.min(maxScroll(), target + e.deltaY * unit));
      if (!animating) { animating = true; requestAnimationFrame(step); }
    }, { passive: false });

    // Keyboard, scrollbar drags and anchor links scroll natively: follow them.
    window.addEventListener('scroll', () => {
      if (!animating) { current = window.scrollY; target = current; }
    }, { passive: true });
  };

  /* ---------- Boot ---------- */

  const boot = () => {
    initOverlays();
    initSearch();
    initCart();
    initCards();
    initRails();
    initReveal();
    initHero();
    initCollection();
    initProduct();
    initRecommendations();
    initSmoothScroll();
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot();

  // Theme editor: re-init behaviour when a section is re-rendered.
  document.addEventListener('shopify:section:load', (e) => { initRails(e.target); initReveal(); initRecommendations(); });
})();
